<script setup lang="ts">

import { IconCopy } from '@shared/ui/icons'
import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { AppBadge } from '@shared/ui/badges'

import PromocodeRowActions from './PromocodeRowActions.vue'
import { useTablePromocodes } from '../model/table-promocodes.hook'

defineOptions({
  name: 'TablePromocodes',
})

const {
  promocodes,
  isLoading,
  hasPromocodes,
  canManage,
  meta,
  perPageOptions,
  formatDateTime,
  getExpiresLabel,
  getStatusColor,
  getStatusText,
  getOrdersLink,
  askEdit,
  setChangePage,
  setChangePerPage,
} = useTablePromocodes()
</script>

<template>
  <div class="table-promocodes">
    <AppTable
      class="rounded-table"
      :meta="meta"
      :per-page-options="perPageOptions"
      :loading="isLoading"
      @change:page="setChangePage"
      @change:per-page="setChangePerPage"
    >
      <template #head>
        <AppTr>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Date of creation
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Expires at
            </AppText>
          </AppTh>
          <AppTh class="text-center">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Discount
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Status
            </AppText>
          </AppTh>
          <AppTh class="text-center">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Orders
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Promo code
            </AppText>
          </AppTh>
          <AppTh class="table-promocodes__actions-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasPromocodes"
        #body
      >
        <AppTr
          v-for="promocode in promocodes"
          :key="promocode.id"
        >
          <AppTd class="medium table-promocodes__date-cell">
            <button
              v-if="canManage"
              type="button"
              class="table-promocodes__link"
              @click="askEdit(promocode)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ formatDateTime(promocode.created_at) }}
              </AppText>
            </button>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ formatDateTime(promocode.created_at) }}
            </AppText>
          </AppTd>
          <AppTd class="medium table-promocodes__date-cell">
            <button
              v-if="canManage"
              type="button"
              class="table-promocodes__link"
              @click="askEdit(promocode)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ getExpiresLabel(promocode) }}
              </AppText>
            </button>
            <AppText
              v-else
              name="body1"
              color="slate-900"
            >
              {{ getExpiresLabel(promocode) }}
            </AppText>
          </AppTd>
          <AppTd class="medium text-center">
            <AppText
              name="body1"
              color="slate-900"
            >
              {{ promocode.discount }}%
            </AppText>
          </AppTd>
          <AppTd class="medium">
            <AppBadge
              :color="getStatusColor(promocode.status)"
              :text="getStatusText(promocode.status)"
            />
          </AppTd>
          <AppTd class="medium text-center">
            <NuxtLink
              class="table-promocodes__orders-link"
              :to="getOrdersLink(promocode)"
            >
              <AppText
                name="body1"
                color="blue-600"
              >
                {{ promocode.orders_count ?? 0 }}
              </AppText>
            </NuxtLink>
          </AppTd>
          <AppTd class="medium">
            <div class="table-promocodes__code">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ promocode.name }}
              </AppText>
              <IconCopy
                :value="promocode.name"
                tooltip="Click to copy code"
              />
            </div>
          </AppTd>
          <AppTd class="zero-padding table-promocodes__actions-cell">
            <PromocodeRowActions
              v-if="canManage"
              :promocode="promocode"
            />
          </AppTd>
        </AppTr>
      </template>
    </AppTable>
  </div>
</template>

<style scoped lang="scss" src="./TablePromocodes.scss" />
